import React, { useState, useCallback, useEffect, useRef } from 'react';

interface DualRangeSliderProps {
  min: number;
  max: number;
  value1: number;
  value2: number;
  onChange: (val1: number, val2: number) => void;
}

const DualRangeSlider: React.FC<DualRangeSliderProps> = ({ min, max, value1, value2, onChange }) => {
  const [isDragging, setIsDragging] = useState<'handle1' | 'handle2' | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  const getPercentage = (value: number) => ((value - min) / (max - min)) * 100;

  const handleMouseDown = (handle: 'handle1' | 'handle2') => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent track click when dragging handles
    setIsDragging(handle);
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    if (!sliderRef.current || isDragging) {
      return; // Don't handle track clicks while dragging
    }

    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const clickedValue = min + (percentage / 100) * (max - min);

    // Calculate which handle is closer to the clicked position
    const distanceToHandle1 = Math.abs(clickedValue - value1);
    const distanceToHandle2 = Math.abs(clickedValue - value2);

    const minBuffer = 0.01;

    if (distanceToHandle1 <= distanceToHandle2) {
      // Move handle1, but don't let it get too close to handle2
      const newValue1 = Math.min(clickedValue, value2 - minBuffer);
      onChange(Math.max(min, newValue1), value2);
    } else {
      // Move handle2, but don't let it get too close to handle1
      const newValue2 = Math.max(clickedValue, value1 + minBuffer);
      onChange(value1, Math.min(max, newValue2));
    }
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !sliderRef.current) {
        return;
      }

      const rect = sliderRef.current.getBoundingClientRect();
      const percentage = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const value = min + (percentage / 100) * (max - min);

      // Minimum buffer between handles (e.g., 0.1 seconds)
      const minBuffer = 0.01;
      if (isDragging === 'handle1') {
        // Handle 1 (left) - ensure it doesn't get too close to handle 2
        const newValue1 = Math.min(value, value2 - minBuffer);
        onChange(Math.max(min, newValue1), value2);
      } else if (isDragging === 'handle2') {
        // Handle 2 (right) - ensure it doesn't get too close to handle 1
        const newValue2 = Math.max(value, value1 + minBuffer);
        onChange(value1, Math.min(max, newValue2));
      }
    },
    [isDragging, min, max, value1, value2, onChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handle1Percentage = getPercentage(value1);
  const handle2Percentage = getPercentage(value2);

  return (
    <div className="relative flex h-6 items-center">
      {/* Track */}
      <div
        ref={sliderRef}
        className="relative h-1.5 w-full cursor-pointer rounded-full bg-gray-700"
        onClick={handleTrackClick}
      >
        {/* Active range between handles */}
        <div
          className="absolute h-1.5 rounded-full bg-gray-300"
          style={{
            left: `${handle1Percentage}%`,
            width: `${handle2Percentage - handle1Percentage}%`
          }}
        />

        {/* Handle 1 */}
        <div
          className="absolute size-4 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-gray-700 bg-white shadow-sm transition-transform hover:scale-110 active:cursor-grabbing"
          style={{ left: `${handle1Percentage}%`, top: '50%' }}
          onMouseDown={handleMouseDown('handle1')}
        />

        {/* Handle 2 */}
        <div
          className="absolute size-4 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-gray-700 bg-white shadow-sm transition-transform hover:scale-110 active:cursor-grabbing"
          style={{ left: `${handle2Percentage}%`, top: '50%' }}
          onMouseDown={handleMouseDown('handle2')}
        />
      </div>
    </div>
  );
};

export default DualRangeSlider;
